import { Column, FieldContainer, Modal, Table } from '@odin-labs/components';
import {
  GetZoneDetailsQuery,
  useGetZoneDetailsQuery,
} from 'apollo/generated/client-operations';
import { LoadingError } from 'components/loadingError';
import React from 'react';

type Zone = NonNullable<GetZoneDetailsQuery['zone']>;
type ZoneConnection = Zone['connections'][number];
type ConnectionColumn = Column<ZoneConnection>;

const connectionColumns: ConnectionColumn[] = [
  {
    id: 'type',
    Header: 'Type',
    accessor: (connection): string => connection.externalEntity.typeDisplayName,
  },
  {
    id: 'connectedTo',
    Header: 'Connected to',
    accessor: (connection): string => connection.externalEntity.name,
    Cell: ({
      value,
      row,
    }: {
      value: string;
      row: { original: ZoneConnection };
    }): React.ReactElement => (
      <span className="odin-block">
        <span className="odin-block odin-text-sm odin-text-gray-900">
          {value}
        </span>
        <span className="odin-block odin-break-all odin-font-mono odin-text-xs odin-text-gray-500">
          {row.original.externalEntity.id}
        </span>
      </span>
    ),
  },
];

export function ZoneDetailsDrawer({
  zoneId,
  onClose,
}: {
  zoneId: string | null;
  onClose: () => void;
}): React.ReactElement {
  const { data, loading, error } = useGetZoneDetailsQuery({
    variables: { zoneId: zoneId ?? '' },
    fetchPolicy: 'no-cache',
    skip: !zoneId,
  });
  const zone = data?.zone ?? null;

  return (
    <Modal
      open={Boolean(zoneId)}
      setOpen={(open) => !open && onClose()}
      variant="drawer"
      size="md"
      title={zone?.name ?? 'Zone'}
      actionsPanel={null}
    >
      {loading && <LoadingError loading />}
      {error && <LoadingError error={error} />}
      {!loading && !error && zoneId && !zone && (
        <p className="odin-text-sm odin-text-gray-600">Zone not found.</p>
      )}
      {!loading && !error && zone && (
        <div className="odin-space-y-10">
          <div className="odin-grid odin-grid-cols-6 odin-gap-6">
            <FieldContainer
              name="name"
              label="Name"
              className="odin-col-span-6 sm:odin-col-span-3"
            >
              <p className="odin-text-sm odin-text-gray-900">{zone.name}</p>
            </FieldContainer>
          </div>

          <section className="odin-space-y-4">
            <h3 className="odin-text-base odin-font-medium odin-text-gray-900">
              Connections
            </h3>
            <Table
              columns={connectionColumns}
              data={zone.connections}
              noResultsText="No connections to show"
              remote
              disableGlobalFilter
              disableSortBy
            />
          </section>
        </div>
      )}
    </Modal>
  );
}
